import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import {
  Heading,
  Button,
  PageHeader,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import {
  Plus as AddIcon,
  Search as SearchIcon,
} from 'lucide-react';
import { useFilings } from '../../hooks';
import { formatDate } from '../../utils/display-format';
import {
  CodeBadge,
  FILING_STATUS_LABEL,
  FILING_STATUS_VARIANT,
  FILING_TYPE_LABEL,
  FilingNumberLink,
  labelFor,
  UccPortfolioMetrics,
  UccTable,
  UccTenantScope,
  type UccFiling,
} from './ucc-display';

/** How many of the most recently updated filings the Recent Activity card lists. */
const RECENT_ACTIVITY_ROWS = 10;

/** Route that opens the Filings list with its create dialog already open. */
export const NEW_FILING_ROUTE = '/ucc/filings?new=1';

function useRecentActivityColumns(): ColumnDef<UccFiling, unknown>[] {
  return useMemo<ColumnDef<UccFiling, unknown>[]>(
    () => [
      {
        accessorKey: 'filingNumber',
        header: 'Filing #',
        cell: ({ row }) => <FilingNumberLink filing={row.original} />,
      },
      {
        accessorKey: 'filingType',
        header: 'Type',
        cell: ({ row }) => (
          <span className="text-sm font-sans text-primary">
            {labelFor(FILING_TYPE_LABEL, row.original.filingType)}
          </span>
        ),
      },
      {
        accessorKey: 'jurisdictionCode',
        header: 'Jurisdiction',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <CodeBadge code={row.original.status} labels={FILING_STATUS_LABEL} variants={FILING_STATUS_VARIANT} />
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-sm font-sans text-secondary">{formatDate(row.original.updatedAt)}</span>
        ),
      },
    ],
    [],
  );
}

export function UCCDashboardPage() {
  const tenantId = useAuthStore(s => s.user?.tenantId) ?? '';
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useFilings({ tenantId, limit: 100 });
  const columns = useRecentActivityColumns();

  const recentFilings = useMemo(
    () =>
      [...(data?.data ?? [])]
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, RECENT_ACTIVITY_ROWS),
    [data],
  );

  return (
    <div>
      <PageHeader
        description="Filing operations overview"
        actions={
          <>
            <Button variant="secondary" onClick={() => navigate('/ucc/search')}>
              <SearchIcon className="mr-2" size={16} />
              Run Search
            </Button>
            <Button variant="primary" onClick={() => navigate(NEW_FILING_ROUTE)}>
              <AddIcon className="mr-2" size={16} />
              New Filing
            </Button>
          </>
        }
      />

      <UccTenantScope tenantId={tenantId} />

      {tenantId && (
        <>
          <UccPortfolioMetrics tenantId={tenantId} />

          <section aria-label="Recent Activity">
            <Heading level={3} size="card" className="mb-4">
              Recent Activity
            </Heading>
            <UccTable<UccFiling>
              columns={columns}
              rows={recentFilings}
              isLoading={isLoading}
              error={error}
              errorTitle="Could not load recent filings"
              onRetry={() => refetch()}
              empty={{
                title: 'No UCC filings for this tenant yet',
                description:
                  'Filings appear here once a pledge is perfected or an operator creates one with New Filing.',
              }}
              getRowId={(row) => row.filingId}
              onRowClick={(row) => navigate(`/ucc/filings/${row.filingId}`)}
            />
          </section>
        </>
      )}
    </div>
  );
}
